import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { portalMainProps, readPortalSidebarCollapsed } from './components/portal/PortalSidebar';
import { LetterGenerationModule } from './components/letters/LetterGenerationModule';
import { useAuth } from './context/AuthContext';
import { QRLetterAccessPage } from './pages/QRLetterAccessPage';
import {
  DEFAULT_LETTER_PAGE,
  buildLetterRouteSearch,
  getLetterPageFromPath,
  getLetterPathForPage,
  isKnownLetterPage,
} from './utils/letterRoutes';
import { CENTRAL_AUTH_ORIGIN } from './utils/sso';

const CENTRAL_LOGIN = `${CENTRAL_AUTH_ORIGIN}/login`;

function getModulePage(page: string): string {
  switch (page) {
    case 'letters-create':
      return 'create';
    case 'letters-create-fresh':
      return 'create-fresh';
    case 'letters-create-template':
      return 'create-template';
    case 'letters-view-templates':
      return 'view-templates';
    case 'letters-upload-pdf':
      return 'upload-pdf';
    case 'letters-view':
      return 'view';
    case 'letters-drafts':
      return 'drafts';
    case 'letters-pending-approval':
      return 'pending-approval';
    case 'letters-admin':
      return 'admin';
    case 'letters-role-assignment':
      return 'role-assignment';
    case 'letters-add-signature':
      return 'add-signature';
    case 'letters-dashboard':
    default:
      return 'dashboard';
  }
}

function App() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [desktopCollapsed, setDesktopCollapsed] = useState<boolean>(readPortalSidebarCollapsed);

  const isQrAccessPage = location.pathname.startsWith('/qr-letter/');
  const pageFromQuery = searchParams.get('page');
  const pageFromPath = getLetterPageFromPath(location.pathname);
  const currentPage = isKnownLetterPage(pageFromQuery)
    ? pageFromQuery!
    : pageFromPath || DEFAULT_LETTER_PAGE;
  const modulePage = useMemo(() => getModulePage(currentPage), [currentPage]);
  const loginUrl = useMemo(() => {
    const next = encodeURIComponent(window.location.href);
    return `${CENTRAL_LOGIN}?next=${next}`;
  }, []);

  useEffect(() => {
    if (isQrAccessPage) return;
    const targetPath = getLetterPathForPage(currentPage);
    const targetSearch = buildLetterRouteSearch(searchParams);
    const currentPath = location.pathname.replace(/\/+$/, '') || '/';
    const shouldReplace = Boolean(pageFromQuery) || !pageFromPath || currentPath !== targetPath;

    if (currentPath !== targetPath || searchParams.has('page')) {
      navigate(
        { pathname: targetPath, search: targetSearch ? `?${targetSearch}` : '' },
        { replace: shouldReplace },
      );
    }
  }, [
    currentPage,
    isQrAccessPage,
    location.pathname,
    navigate,
    pageFromPath,
    pageFromQuery,
    searchParams,
  ]);

  const handlePageChange = (page: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    if (page !== 'letters-create') {
      params.delete('letterId');
    }
    const search = buildLetterRouteSearch(params);
    navigate({ pathname: getLetterPathForPage(page), search: search ? `?${search}` : '' });
  };

  if (isQrAccessPage) {
    return <QRLetterAccessPage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">Checking Session</h1>
          <p className="mt-3 text-slate-600">Please wait while we verify your login.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">Letter Portal Login Required</h1>
          <p className="mt-3 text-slate-600">
            Your session is not active. Please login to continue.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => {
                window.location.href = loginUrl;
              }}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Login To Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        currentPage={currentPage}
        onPageChange={handlePageChange}
        desktopCollapsed={desktopCollapsed}
        onDesktopCollapsedChange={setDesktopCollapsed}
      />
      <main className="flex-1 min-w-0" {...portalMainProps(!desktopCollapsed)}>
        <LetterGenerationModule page={modulePage} onNavigate={handlePageChange} />
      </main>
    </div>
  );
}

export default App;
